/**
 * "My Itineraries" — the calling user's itineraries (plan U5). Each item links
 * to the read view (`/itineraries/:slug`) and the editor
 * (`/itineraries/:slug/edit`). "New itinerary" creates via
 * `trpc.itinerary.create` then navigates to the new slug's editor. Archived
 * itineraries are opt-in via the includeArchived toggle.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import { PageContainer } from '../PageContainer';
import { Heading1, BodyText, Caption } from '../ui/Typography';
import { Button } from '../ui/Button';

export const MyItinerariesList = () => {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [showNew, setShowNew] = useState(false);

  const list = trpc.itinerary.listMine.useQuery({
    limit: 50,
    offset: 0,
    includeArchived,
  });

  const create = trpc.itinerary.create.useMutation({
    onSuccess: (created) => {
      utils.itinerary.listMine.invalidate();
      navigate(`/itineraries/${created.publicSlug}/edit`);
    },
  });

  const titleValid = newTitle.trim().length > 0;

  return (
    <div className="px-0 md:px-4 pt-2 pb-4">
      <PageContainer className="mt-2">
        <div className="border-b border-[#DBD4C9] pb-4">
          {/* Action buttons — TOP RIGHT (repo form rule). */}
          <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="h-4 w-4"
              />
              <Caption className="text-gray-600">Show archived</Caption>
            </label>
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New itinerary
            </Button>
          </div>

          <Heading1>My Itineraries</Heading1>

          {showNew && (
            <div className="mt-4 rounded-lg border border-[#DBD4C9] bg-[#F2F0EA] p-4">
              <div className="mb-3 flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={!titleValid || create.isPending}
                  loading={create.isPending}
                  onClick={() => create.mutate({ title: newTitle.trim(), regionRefs: [] })}
                >
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowNew(false);
                    setNewTitle('');
                  }}
                >
                  Cancel
                </Button>
              </div>
              {create.isError && (
                <Caption className="mb-2 block text-error">
                  Couldn&apos;t create the itinerary. Try again.
                </Caption>
              )}
              <input
                type="text"
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Itinerary title (e.g. Napa weekend)"
                className={`w-full rounded-lg border bg-gray-50 px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-info-500 ${
                  titleValid ? 'border-success-500' : 'border-[#DBD4C9]'
                }`}
              />
            </div>
          )}
        </div>

        <div className="mt-4">
          {list.isLoading && (
            <BodyText className="text-gray-500">Loading…</BodyText>
          )}
          {list.isError && (
            <BodyText className="text-error">
              Couldn&apos;t load your itineraries. Refresh to try again.
            </BodyText>
          )}
          {list.data?.items.length === 0 && (
            <BodyText className="text-gray-600">
              You haven&apos;t created any itineraries yet. Start with
              &ldquo;New itinerary&rdquo; above.
            </BodyText>
          )}

          <ul className="space-y-3">
            {list.data?.items.map((item) => (
              <li
                key={item._id}
                className="rounded-lg border border-[#DBD4C9] bg-gray-50 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <BodyText className="text-gray-900">
                      {item.title}
                    </BodyText>
                    <Caption className="mt-1 block text-gray-600">
                      {item.days.length}{' '}
                      {item.days.length === 1 ? 'day' : 'days'} ·{' '}
                      {item.days.reduce((n, d) => n + d.stops.length, 0)} stops
                      {' · '}
                      {item.audience}
                      {item.editorialStatus === 'archived' && ' · archived'}
                    </Caption>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigate(`/itineraries/${item.publicSlug}`)
                      }
                    >
                      View
                    </Button>
                    <Button
                      size="sm"
                      variant="primary"
                      onClick={() =>
                        navigate(`/itineraries/${item.publicSlug}/edit`)
                      }
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </PageContainer>
    </div>
  );
};
